import { createAction, createReducer, type AnyAction } from '@reduxjs/toolkit';
import {
  getCrossAssetRiskGroupsForAsset,
  isActiveCrossAssetRiskGroup,
  isMonitoredAssetSecurity,
} from '@src/components/AutoHedging/crossAssetRiskGroup';
import type { CrossAssetRiskGroup } from '@src/containers/Admin/TradingLimits/SubAccountTradingLimits/crossAssetRiskGroupsApi';
import {
  BaseField,
  EMPTY_ARRAY,
  EMPTY_MAP,
  getAllChildrenOfSubAccount,
  getDoubleMapValues,
  getTypedKeys,
  HedgeCommandEnum,
  ModeEnum,
  NumericField,
  SelectorField,
  SubAccountTypeEnum,
  type AggregationWithAccounts,
  type Currency,
  type IHedgeRule,
  type ISubaccount,
  type Security,
  type SubAccount,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { uniq } from 'lodash-es';
import { useEffect } from 'react';
import { DEFAULT_POSITION_HEDGER_HOME_CURRENCY } from '../../hooks/useFeatureFlag';
import { getHedgeSecurityOptions } from './hedgeSymbol';
import type {
  EditIHedgeRuleForm,
  HedgeMonitoredAsset,
  InitReducerPayload,
  PositionAutoHedgingDrawerState,
  PositionAutoHedgingReferenceData,
  UpdateFieldPayload,
} from './types';
import { selectHedgeSymbol } from './utils';
import { performValidation } from './validation';

export const HEDGECOMMAND_ARRAY = [HedgeCommandEnum.Kill] satisfies HedgeCommandEnum[];

const blankState = {
  form: {
    Asset: new SelectorField({ idProperty: 'Symbol', name: 'Asset', isRequired: true }),
    TargetAsset: new SelectorField({ idProperty: 'Symbol', name: 'Target Asset', isRequired: true }),
    TargetAccount: new SelectorField({ idProperty: 'SubaccountID', name: 'Subaccount', isRequired: true }),
    HedgeStrategy: new SelectorField({ idProperty: 'Name', name: 'Strategy', isRequired: true }),
    LongPositionLimit: new NumericField({ name: 'Long Threshold', isRequired: true }),
    ShortPositionLimit: new NumericField({ name: 'Short Threshold', isRequired: true }),
    TargetPosition: new NumericField({ name: 'Target Position', isRequired: true }),
    AccountID: new SelectorField({ idProperty: 'SubaccountID', name: 'Account', isRequired: true }),
    HedgeAggregation: new SelectorField<AggregationWithAccounts>({
      idProperty: 'Name',
      name: 'Aggregation',
      isRequired: true,
    }),
    CrossAssetRiskGroup: new SelectorField<CrossAssetRiskGroup>({
      idProperty: 'Name',
      name: 'Cross-Asset Risk Group',
      isRequired: false,
    }),
    HedgeSymbol: new SelectorField<Security>({
      idProperty: 'Symbol',
      name: 'Hedge Security',
      isRequired: false,
    }),
  },
  command: {
    mode: ModeEnum.Disabled,
  },
  hedgeRuleBeingChanged: null,
  referenceData: {
    currencies: EMPTY_ARRAY,
    currenciesBySymbol: EMPTY_MAP,
    strategies: EMPTY_ARRAY,
    subaccounts: EMPTY_ARRAY,
    aggregations: EMPTY_ARRAY,
    rollupMembershipsByParentChild: EMPTY_MAP,
    spotTradingPairsByBaseCurrency: EMPTY_MAP,
    spotTradingPairsByQuoteCurrency: EMPTY_MAP,
    securitiesBySymbol: EMPTY_MAP,
    searchableSecurities: EMPTY_ARRAY,
    positionHedgerHomeCurrency: DEFAULT_POSITION_HEDGER_HOME_CURRENCY,
    crossAssetRiskGroups: EMPTY_ARRAY,
    enableHedgeRuleCrossAssetRiskGroup: false,
    enableCrossAssetRiskGroupDeltaRiskCheck: false,
  },
} satisfies PositionAutoHedgingDrawerState;

export const getInitialState = (payload?: InitReducerPayload): PositionAutoHedgingDrawerState => {
  if (!payload) {
    return blankState;
  }
  const {
    hedgeRule,
    currencies = EMPTY_ARRAY,
    strategies = EMPTY_ARRAY,
    subaccounts = EMPTY_ARRAY,
    aggregations = EMPTY_ARRAY,
    rollupMembershipsByParentChild = EMPTY_MAP,
    spotTradingPairsByBaseCurrency = EMPTY_MAP,
    spotTradingPairsByQuoteCurrency = EMPTY_MAP,
    securitiesBySymbol = EMPTY_MAP,
    searchableSecurities = EMPTY_ARRAY,
    currenciesBySymbol = EMPTY_MAP,
    positionHedgerHomeCurrency = DEFAULT_POSITION_HEDGER_HOME_CURRENCY,
    crossAssetRiskGroups = EMPTY_ARRAY,
    enableHedgeRuleCrossAssetRiskGroup = false,
    enableCrossAssetRiskGroupDeltaRiskCheck = false,
  } = payload;
  const {
    Asset,
    TargetAsset,
    TargetAccount,
    HedgeStrategy,
    LongPositionLimit,
    ShortPositionLimit,
    TargetPosition,
    AccountID,
    HedgeAggregation,
    CrossAssetRiskGroup,
    HedgeSymbol,
  } = blankState.form;
  const hedgeStrategyField = HedgeStrategy.updateAvailableItems(strategies).updateValueFromID(hedgeRule?.HedgeStrategy);
  const longPositionLimitField = LongPositionLimit.updateValue(hedgeRule?.LongPositionLimit);
  const shortPositionLimitField = ShortPositionLimit.updateValue(hedgeRule?.ShortPositionLimit);
  const targetPositionField = TargetPosition.updateValue(hedgeRule?.TargetPosition);
  const hedgeAggregationField = HedgeAggregation.updateAvailableItems(aggregations, {
    preventDefaultToValue: true,
  }).updateValueFromID(hedgeRule?.HedgeAggregation);

  const { assetField, targetAssetField } = getAssetAndTargetAsset({
    Asset,
    TargetAsset,
    referenceData: {
      spotTradingPairsByBaseCurrency,
      spotTradingPairsByQuoteCurrency,
      currenciesBySymbol,
      currencies,
      positionHedgerHomeCurrency,
      securitiesBySymbol,
      searchableSecurities,
    },
    newAsset: hedgeRule?.Asset,
    newTargetAsset: hedgeRule?.TargetAsset,
  });

  const { accountField, targetAccountField } = getAccountAndTargetAccount({
    AccountID,
    TargetAccount,
    referenceData: {
      subaccounts,
      rollupMembershipsByParentChild,
    },
    newAccountID: hedgeRule?.AccountID,
    newTargetAccountID: hedgeRule?.TargetAccountID,
  });

  const crossAssetRiskGroupField = getCrossAssetRiskGroupField({
    CrossAssetRiskGroup,
    assetSymbol: assetField.value?.Symbol,
    crossAssetRiskGroups,
    enableHedgeRuleCrossAssetRiskGroup,
    selectedGroupName: hedgeRule?.CrossAssetRiskGroup,
    disabled: !!hedgeRule,
    securitiesBySymbol,
    currenciesBySymbol,
  });

  const hedgeSymbolField = getHedgeSymbolField({
    HedgeSymbol,
    asset: assetField.value,
    targetAsset: targetAssetField.value,
    group: crossAssetRiskGroupField.value,
    selectedSymbol: hedgeRule?.HedgeSymbol,
    preserveSelection: !!hedgeRule,
    referenceData: {
      searchableSecurities,
      securitiesBySymbol,
      currenciesBySymbol,
      spotTradingPairsByBaseCurrency,
      positionHedgerHomeCurrency,
      enableCrossAssetRiskGroupDeltaRiskCheck,
    },
  });

  return {
    form: {
      Asset: assetField.setTouched(false).setDisabled(!!hedgeRule),
      TargetAsset: targetAssetField.setTouched(false),
      TargetAccount: targetAccountField.setTouched(false),
      HedgeStrategy: hedgeStrategyField.setTouched(false),
      LongPositionLimit: longPositionLimitField.setTouched(false),
      ShortPositionLimit: shortPositionLimitField.setTouched(false),
      TargetPosition: targetPositionField.setTouched(false),
      AccountID: accountField.setTouched(false).setDisabled(!!hedgeRule),
      HedgeAggregation: hedgeAggregationField.setTouched(false),
      CrossAssetRiskGroup: crossAssetRiskGroupField.setTouched(false),
      HedgeSymbol: hedgeSymbolField.setTouched(false),
    },
    command: {
      mode: hedgeRule?.Mode ?? ModeEnum.Disabled,
    },
    hedgeRuleBeingChanged: hedgeRule ?? null,
    referenceData: {
      currencies,
      currenciesBySymbol,
      strategies,
      subaccounts,
      aggregations,
      rollupMembershipsByParentChild,
      spotTradingPairsByBaseCurrency,
      spotTradingPairsByQuoteCurrency,
      securitiesBySymbol,
      searchableSecurities,
      positionHedgerHomeCurrency,
      crossAssetRiskGroups,
      enableHedgeRuleCrossAssetRiskGroup,
      enableCrossAssetRiskGroupDeltaRiskCheck,
    },
  };
};

export const setHedgeRule = createAction<IHedgeRule>('setHedgeRule');
export const resetForm = createAction('resetForm');
export const updateField = createAction<UpdateFieldPayload>('updateField');
export const updateMode = createAction<ModeEnum>('updateMode');
export const updateHedgeCommand = createAction<(typeof HEDGECOMMAND_ARRAY)[number] | undefined>('updateHedgeCommand');
export const init = createAction<InitReducerPayload>('init');
export const touchAll = createAction('touchAll');
export const updateDependencies = createAction<PositionAutoHedgingReferenceData>('updateDependencies');

export const reducer = createReducer<PositionAutoHedgingDrawerState>(blankState, builder => {
  return builder
    .addCase(init, (state, { payload }) => {
      return getInitialState(payload);
    })
    .addCase(resetForm, state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          const value = state.form[key].updateValue(undefined).setDisabled(false).setTouched(false);
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = value;
        }
      });

      state.command = {
        mode: ModeEnum.Enabled,
      };
      state.hedgeRuleBeingChanged = undefined;

      return state;
    })
    .addCase(setHedgeRule, (state, { payload: hedgeRule }) => {
      state.hedgeRuleBeingChanged = hedgeRule;

      handleChangeAccountID({
        state,
        newAccountID: hedgeRule.AccountID,
        newTargetAccountID: hedgeRule.TargetAccountID,
      });
      handleChangeAsset({
        state,
        newAssetSymbol: hedgeRule.Asset,
        newTargetAssetSymbol: hedgeRule.TargetAsset,
        selectedCrossAssetRiskGroupName: hedgeRule.CrossAssetRiskGroup,
      });

      // These are disabled when modifying a hedge rule.
      state.form.AccountID = state.form.AccountID.setDisabled(!!hedgeRule);
      state.form.Asset = state.form.Asset.setDisabled(!!hedgeRule);

      state.form.HedgeStrategy = state.form.HedgeStrategy.updateValueFromID(hedgeRule?.HedgeStrategy);
      state.form.LongPositionLimit = state.form.LongPositionLimit.updateValue(hedgeRule?.LongPositionLimit);
      state.form.ShortPositionLimit = state.form.ShortPositionLimit.updateValue(hedgeRule?.ShortPositionLimit);
      state.form.TargetPosition = state.form.TargetPosition.updateValue(hedgeRule?.TargetPosition);
      state.form.HedgeAggregation = state.form.HedgeAggregation.updateValueFromID(hedgeRule?.HedgeAggregation);
      syncHedgeSymbolField(state, hedgeRule.HedgeSymbol);

      state.command = {
        mode: hedgeRule?.Mode ?? ModeEnum.Disabled,
      };
    })
    .addCase(touchAll, state => {
      getTypedKeys(state.form).forEach(key => {
        if (state.form[key] instanceof BaseField) {
          // @ts-expect-error - BaseField methods have trouble lining up on keyed calls
          state.form[key] = state.form[key].setTouched(true);
        }
      });
      return state;
    })
    .addCase(updateField, (state, action) => {
      const field = action.payload.key;
      switch (field) {
        case 'TargetAccount': {
          state.form.TargetAccount = state.form.TargetAccount.updateValue(action.payload.value);
          break;
        }
        case 'ShortPositionLimit': {
          state.form.ShortPositionLimit = state.form.ShortPositionLimit.updateValue(action.payload.value);
          state.form.TargetPosition = state.form.TargetPosition.setTouched(true);
          state.form.LongPositionLimit = state.form.LongPositionLimit.setTouched(true);
          state.form.ShortPositionLimit = state.form.ShortPositionLimit.setTouched(true);
          break;
        }
        case 'TargetPosition': {
          state.form.TargetPosition = state.form.TargetPosition.updateValue(action.payload.value);
          state.form.TargetPosition = state.form.TargetPosition.setTouched(true);
          state.form.LongPositionLimit = state.form.LongPositionLimit.setTouched(true);
          state.form.ShortPositionLimit = state.form.ShortPositionLimit.setTouched(true);
          break;
        }
        case 'LongPositionLimit': {
          state.form.LongPositionLimit = state.form.LongPositionLimit.updateValue(action.payload.value);
          state.form.TargetPosition = state.form.TargetPosition.setTouched(true);
          state.form.LongPositionLimit = state.form.LongPositionLimit.setTouched(true);
          state.form.ShortPositionLimit = state.form.ShortPositionLimit.setTouched(true);
          break;
        }
        case 'TargetAsset': {
          state.form.TargetAsset = state.form.TargetAsset.updateValue(action.payload.value);
          syncHedgeSymbolField(state);
          break;
        }
        case 'Asset': {
          state.form.Asset = state.form.Asset.updateValue(action.payload.value);
          // whenever this changes, update the available items of TargetAsset
          handleChangeAsset({
            state,
            newAssetSymbol: action.payload.value?.Symbol,
            newTargetAssetSymbol: state.form.TargetAsset.value?.Symbol,
          });
          break;
        }
        case 'HedgeStrategy': {
          state.form.HedgeStrategy = state.form.HedgeStrategy.updateValue(action.payload.value);
          break;
        }
        case 'AccountID': {
          state.form.AccountID = state.form.AccountID.updateValue(action.payload.value);
          handleChangeAccountID({ state, newAccountID: action.payload.value?.SubaccountID });
          break;
        }
        case 'HedgeAggregation': {
          state.form.HedgeAggregation = state.form.HedgeAggregation.updateValue(action.payload.value);
          break;
        }
        case 'CrossAssetRiskGroup': {
          state.form.CrossAssetRiskGroup = state.form.CrossAssetRiskGroup.updateValue(action.payload.value);
          state.form.HedgeSymbol = state.form.HedgeSymbol.updateValue(undefined, true);
          syncHedgeSymbolField(state);
          break;
        }
        case 'HedgeSymbol': {
          state.form.HedgeSymbol = state.form.HedgeSymbol.updateValue(action.payload.value);
          break;
        }
        default: {
          const _typecheck: never = field;
        }
      }

      performValidation(state);

      return state;
    })
    .addCase(updateMode, (state, action) => {
      state.command.mode = action.payload;
      return state;
    })
    .addCase(updateDependencies, (state, action) => {
      state.referenceData = action.payload;

      const { accountField, targetAccountField } = getAccountAndTargetAccount({
        AccountID: state.form.AccountID,
        TargetAccount: state.form.TargetAccount,
        newAccountID: state.form.AccountID.value?.SubaccountID,
        newTargetAccountID: state.form.TargetAccount.value?.SubaccountID,
        referenceData: action.payload,
      });
      state.form.TargetAccount = targetAccountField.setTouched(false);
      state.form.AccountID = accountField.setTouched(false);

      const { assetField, targetAssetField } = getAssetAndTargetAsset({
        Asset: state.form.Asset,
        TargetAsset: state.form.TargetAsset,
        newAsset: state.form.Asset.value?.Symbol,
        newTargetAsset: state.form.TargetAsset.value?.Symbol,
        referenceData: action.payload,
      });

      state.form.Asset = assetField.setTouched(false);
      state.form.TargetAsset = targetAssetField.setTouched(false);

      state.form.HedgeAggregation = state.form.HedgeAggregation.updateAvailableItems(action.payload.aggregations, {
        preventDefaultToValue: true,
      });
      state.form.HedgeStrategy = state.form.HedgeStrategy.updateAvailableItems(action.payload.strategies, {
        keepIsTouched: true,
        preventDefaultToValue: true,
      });

      state.form.CrossAssetRiskGroup = getCrossAssetRiskGroupField({
        CrossAssetRiskGroup: state.form.CrossAssetRiskGroup,
        assetSymbol: state.form.Asset.value?.Symbol,
        crossAssetRiskGroups: action.payload.crossAssetRiskGroups,
        enableHedgeRuleCrossAssetRiskGroup: action.payload.enableHedgeRuleCrossAssetRiskGroup,
        selectedGroupName:
          state.hedgeRuleBeingChanged?.CrossAssetRiskGroup ?? state.form.CrossAssetRiskGroup.value?.Name,
        disabled: !!state.hedgeRuleBeingChanged,
        securitiesBySymbol: action.payload.securitiesBySymbol,
        currenciesBySymbol: action.payload.currenciesBySymbol,
      }).setTouched(false);

      const hedgeSymbolToRestore =
        !state.form.HedgeSymbol.isTouched && state.hedgeRuleBeingChanged
          ? state.hedgeRuleBeingChanged.HedgeSymbol
          : undefined;
      syncHedgeSymbolField(state, hedgeSymbolToRestore);

      performValidation(state);

      return state;
    });
});

/**
 * Calls and applies getAssetAndTargetAsset
 **/
function handleChangeAsset({
  state,
  newAssetSymbol,
  newTargetAssetSymbol,
  selectedCrossAssetRiskGroupName,
}: {
  state: WritableDraft<PositionAutoHedgingDrawerState>;
  /** rollup */
  newAssetSymbol?: Currency['Symbol'];
  /** book */
  newTargetAssetSymbol?: Currency['Symbol'];
  selectedCrossAssetRiskGroupName?: string;
}) {
  const { assetField, targetAssetField } = getAssetAndTargetAsset({
    referenceData: state.referenceData,
    Asset: state.form.Asset,
    TargetAsset: state.form.TargetAsset,
    newAsset: newAssetSymbol,
    newTargetAsset: newTargetAssetSymbol,
  });
  state.form.Asset = assetField;
  state.form.TargetAsset = targetAssetField;
  state.form.CrossAssetRiskGroup = getCrossAssetRiskGroupField({
    CrossAssetRiskGroup: state.form.CrossAssetRiskGroup,
    assetSymbol: assetField.value?.Symbol,
    crossAssetRiskGroups: state.referenceData.crossAssetRiskGroups,
    enableHedgeRuleCrossAssetRiskGroup: state.referenceData.enableHedgeRuleCrossAssetRiskGroup,
    selectedGroupName: selectedCrossAssetRiskGroupName,
    // Immutable while editing an existing rule; otherwise enable once Asset + flag allow it.
    disabled: !!state.hedgeRuleBeingChanged,
    securitiesBySymbol: state.referenceData.securitiesBySymbol,
    currenciesBySymbol: state.referenceData.currenciesBySymbol,
  });
  syncHedgeSymbolField(state);
}

function getCrossAssetRiskGroupField({
  CrossAssetRiskGroup,
  assetSymbol,
  crossAssetRiskGroups,
  enableHedgeRuleCrossAssetRiskGroup,
  selectedGroupName,
  disabled,
  securitiesBySymbol,
  currenciesBySymbol,
}: {
  CrossAssetRiskGroup: WritableDraft<SelectorField<CrossAssetRiskGroup>> | SelectorField<CrossAssetRiskGroup>;
  assetSymbol: string | undefined;
  crossAssetRiskGroups: CrossAssetRiskGroup[];
  enableHedgeRuleCrossAssetRiskGroup: boolean;
  selectedGroupName: string | undefined;
  disabled: boolean;
  securitiesBySymbol: Map<string, Security>;
  currenciesBySymbol: Map<string, Currency>;
}): EditIHedgeRuleForm['CrossAssetRiskGroup'] {
  const isSecurityAsset = isMonitoredAssetSecurity(assetSymbol, securitiesBySymbol, currenciesBySymbol);
  const availableGroups =
    enableHedgeRuleCrossAssetRiskGroup && !isSecurityAsset
      ? getCrossAssetRiskGroupsForAsset(crossAssetRiskGroups, assetSymbol)
      : [];
  const selectedName =
    !isSecurityAsset && isActiveCrossAssetRiskGroup(selectedGroupName) ? selectedGroupName : undefined;
  let field = CrossAssetRiskGroup.updateAvailableItems(availableGroups, {
    preventDefaultToValue: true,
  }).updateValueFromID(selectedName, true);

  const shouldDisable = disabled || !enableHedgeRuleCrossAssetRiskGroup || !assetSymbol || isSecurityAsset;
  field = field.setDisabled(shouldDisable);
  return field;
}

function syncHedgeSymbolField(state: WritableDraft<PositionAutoHedgingDrawerState>, selectedSymbol?: string) {
  state.form.HedgeSymbol = getHedgeSymbolField({
    HedgeSymbol: state.form.HedgeSymbol,
    asset: state.form.Asset.value,
    targetAsset: state.form.TargetAsset.value,
    group: state.form.CrossAssetRiskGroup.value,
    selectedSymbol: selectedSymbol ?? state.form.HedgeSymbol.value?.Symbol,
    preserveSelection: !!state.hedgeRuleBeingChanged,
    referenceData: state.referenceData,
  });
}

function getHedgeSymbolField({
  HedgeSymbol,
  asset,
  targetAsset,
  group,
  selectedSymbol,
  preserveSelection = false,
  referenceData,
}: {
  HedgeSymbol: WritableDraft<SelectorField<Security>> | SelectorField<Security>;
  asset: EditIHedgeRuleForm['Asset']['value'];
  targetAsset: EditIHedgeRuleForm['TargetAsset']['value'];
  group: CrossAssetRiskGroup | undefined;
  selectedSymbol: string | undefined;
  preserveSelection?: boolean;
  referenceData: Pick<
    PositionAutoHedgingReferenceData,
    | 'searchableSecurities'
    | 'securitiesBySymbol'
    | 'currenciesBySymbol'
    | 'spotTradingPairsByBaseCurrency'
    | 'positionHedgerHomeCurrency'
    | 'enableCrossAssetRiskGroupDeltaRiskCheck'
  >;
}): EditIHedgeRuleForm['HedgeSymbol'] {
  const isSecurityAsset = isMonitoredAssetSecurity(
    asset?.Symbol,
    referenceData.securitiesBySymbol,
    referenceData.currenciesBySymbol
  );
  const isGrouped = isActiveCrossAssetRiskGroup(group?.Name);
  let options = getHedgeSecurityOptions(
    asset,
    group,
    referenceData.searchableSecurities,
    referenceData.securitiesBySymbol,
    referenceData.currenciesBySymbol,
    referenceData.enableCrossAssetRiskGroupDeltaRiskCheck
  );
  const selectedSecurity = selectedSymbol ? referenceData.securitiesBySymbol.get(selectedSymbol) : undefined;
  if (preserveSelection && selectedSecurity && !options.some(security => security.Symbol === selectedSecurity.Symbol)) {
    options = [...options, selectedSecurity];
  }
  const wasTouched = HedgeSymbol.isTouched;
  let nextSymbol: string | undefined;
  if (isSecurityAsset) {
    nextSymbol = asset?.Symbol;
  } else if (isGrouped) {
    nextSymbol = wasTouched || preserveSelection ? selectedSymbol : undefined;
  } else {
    nextSymbol = selectHedgeSymbol({
      form: { Asset: { value: asset }, TargetAsset: { value: targetAsset } },
      referenceData,
    } as PositionAutoHedgingDrawerState);
  }

  return HedgeSymbol.setIsRequired(isGrouped)
    .updateAvailableItems(options, { preventDefaultToValue: true })
    .updateValueFromID(nextSymbol, !(isGrouped && wasTouched))
    .setDisabled(!asset || isSecurityAsset || !isGrouped);
}

function getMonitoredAssetOptions(
  referenceData: Pick<
    PositionAutoHedgingReferenceData,
    'currencies' | 'searchableSecurities' | 'positionHedgerHomeCurrency' | 'securitiesBySymbol'
  >,
  selectedSymbol?: string
): EditIHedgeRuleForm['Asset']['availableItems'] {
  const currencies = referenceData.currencies.filter(c => c.Symbol !== referenceData.positionHedgerHomeCurrency);
  const currencySymbols = new Set(referenceData.currencies.map(currency => currency.Symbol));
  const securities = referenceData.searchableSecurities.filter(security => !currencySymbols.has(security.Symbol));
  // Expired/soft-deleted securities stay in securitiesBySymbol but drop out of searchableSecurities.
  const selectedSecurity =
    selectedSymbol && !currencySymbols.has(selectedSymbol)
      ? referenceData.securitiesBySymbol.get(selectedSymbol)
      : undefined;
  if (selectedSecurity && !securities.some(security => security.Symbol === selectedSecurity.Symbol)) {
    securities.push(selectedSecurity);
  }
  return [...currencies, ...securities];
}

function getTargetAssetCurrencies(
  assetKey: string | undefined,
  referenceData: Pick<
    PositionAutoHedgingReferenceData,
    'spotTradingPairsByBaseCurrency' | 'spotTradingPairsByQuoteCurrency' | 'currenciesBySymbol' | 'securitiesBySymbol'
  >
): Currency[] {
  if (!assetKey) {
    return [];
  }

  const security = referenceData.securitiesBySymbol.get(assetKey);
  if (security) {
    return uniq(
      [security.QuoteCurrency, security.SettlementCurrency, security.PositionCurrency].filter(
        (symbol): symbol is string => !!symbol
      )
    )
      .map(symbol => referenceData.currenciesBySymbol.get(symbol))
      .filter((currency): currency is Currency => currency != null);
  }

  const spotTradingPairsBase = getDoubleMapValues(referenceData.spotTradingPairsByBaseCurrency, assetKey);
  const spotTradingPairsQuote = getDoubleMapValues(referenceData.spotTradingPairsByQuoteCurrency, assetKey);
  return uniq(
    spotTradingPairsBase
      .map(pair => pair.QuoteCurrency)
      .concat(spotTradingPairsQuote.map(pair => pair.BaseCurrency))
      .concat([assetKey])
  )
    .map(currency => referenceData.currenciesBySymbol.get(currency))
    .filter((currency): currency is Currency => currency != null);
}

/**
 * Based on the asset, the target asset available items are updated to be possible pairs.
 */
function getAssetAndTargetAsset({
  Asset,
  TargetAsset,
  referenceData,
  newAsset,
  newTargetAsset,
}: {
  Asset: WritableDraft<SelectorField<HedgeMonitoredAsset>> | SelectorField<HedgeMonitoredAsset>;
  TargetAsset: WritableDraft<SelectorField<Currency>> | SelectorField<Currency>;
  referenceData: Pick<
    PositionAutoHedgingReferenceData,
    | 'currencies'
    | 'spotTradingPairsByBaseCurrency'
    | 'spotTradingPairsByQuoteCurrency'
    | 'currenciesBySymbol'
    | 'positionHedgerHomeCurrency'
    | 'securitiesBySymbol'
    | 'searchableSecurities'
  >;
  newAsset: string | undefined;
  newTargetAsset: Currency['Symbol'] | undefined;
}): { targetAssetField: EditIHedgeRuleForm['TargetAsset']; assetField: EditIHedgeRuleForm['Asset'] } {
  const assetKey = newAsset ?? Asset.value?.Symbol;

  const assetField = Asset.updateAvailableItems(getMonitoredAssetOptions(referenceData, assetKey)).updateValueFromID(
    assetKey
  );
  const availableCurrencies = getTargetAssetCurrencies(assetField.value?.Symbol ?? assetKey, referenceData);

  let targetAssetField = TargetAsset.updateAvailableItems(availableCurrencies).updateValueFromID(newTargetAsset, true);

  if (!assetField.value?.Symbol) {
    targetAssetField = targetAssetField.setDisabled(true);
  } else {
    targetAssetField = targetAssetField.setDisabled(false);
  }

  return { targetAssetField, assetField };
}

/**
 * AccountID is a rollup, targetAccount is a book, we narrow the selection of books based on the rollup.
 * Every time accountID change, we need to update the available books. This function updates relevant fields
 * appropriately based on the new accountID and targetAccountID.
 */
function getAccountAndTargetAccount({
  AccountID,
  TargetAccount,
  referenceData,
  newAccountID,
  newTargetAccountID,
}: {
  AccountID: WritableDraft<SelectorField<ISubaccount>>;
  TargetAccount: WritableDraft<SelectorField<ISubaccount>>;
  referenceData: Pick<PositionAutoHedgingReferenceData, 'rollupMembershipsByParentChild' | 'subaccounts'>;
  /** rollup */
  newAccountID: SubAccount['SubaccountID'] | undefined;
  /** book */
  newTargetAccountID: SubAccount['SubaccountID'] | undefined;
}) {
  const rollups = referenceData.subaccounts.filter(s => s.Type === SubAccountTypeEnum.Rollup);
  const accountField = AccountID.updateAvailableItems(rollups).updateValueFromID(newAccountID);

  let targetAccountField = TargetAccount;
  const effectiveNewAccountID = accountField.value?.SubaccountID;

  if (effectiveNewAccountID !== undefined) {
    const booksSet = getAllChildrenOfSubAccount(effectiveNewAccountID, referenceData.rollupMembershipsByParentChild);
    const books = referenceData.subaccounts.filter(
      s => booksSet.has(s.SubaccountID) && s.Type === SubAccountTypeEnum.Book
    );
    targetAccountField = targetAccountField.updateAvailableItems(books).setDisabled(false);
  } else {
    targetAccountField = targetAccountField.setDisabled(true);
  }
  if (newTargetAccountID !== undefined) {
    targetAccountField = targetAccountField.updateValueFromID(newTargetAccountID);
  }

  return { targetAccountField, accountField };
}

/**
 * Calls and applies getAccountAndTargetAccount
 **/
function handleChangeAccountID({
  state,
  newAccountID,
  newTargetAccountID,
}: {
  state: WritableDraft<PositionAutoHedgingDrawerState>;
  /** rollup */
  newAccountID?: SubAccount['SubaccountID'];
  /** book */
  newTargetAccountID?: SubAccount['SubaccountID'];
}) {
  const { accountField, targetAccountField } = getAccountAndTargetAccount({
    AccountID: state.form.AccountID,
    TargetAccount: state.form.TargetAccount,
    referenceData: state.referenceData,
    newAccountID: newAccountID,
    newTargetAccountID: newTargetAccountID,
  });
  state.form.AccountID = accountField;
  state.form.TargetAccount = targetAccountField;
}
/**
 * Hook to update the dependencies of the drawer state for the PositionAutoHedgingDrawer.
 */
export function usePositionAutoHedgingDrawerDependencies({
  currencies,
  currenciesBySymbol,
  strategies,
  subaccounts,
  aggregations,
  rollupMembershipsByParentChild,
  spotTradingPairsByBaseCurrency,
  spotTradingPairsByQuoteCurrency,
  securitiesBySymbol,
  searchableSecurities,
  positionHedgerHomeCurrency,
  crossAssetRiskGroups,
  enableHedgeRuleCrossAssetRiskGroup,
  enableCrossAssetRiskGroupDeltaRiskCheck,
  dispatch,
}: { dispatch: React.Dispatch<AnyAction> } & PositionAutoHedgingReferenceData) {
  useEffect(() => {
    dispatch(
      updateDependencies({
        currencies,
        currenciesBySymbol,
        strategies,
        subaccounts,
        aggregations,
        rollupMembershipsByParentChild,
        spotTradingPairsByBaseCurrency,
        spotTradingPairsByQuoteCurrency,
        securitiesBySymbol,
        searchableSecurities,
        positionHedgerHomeCurrency,
        crossAssetRiskGroups,
        enableHedgeRuleCrossAssetRiskGroup,
        enableCrossAssetRiskGroupDeltaRiskCheck,
      })
    );
  }, [
    dispatch,
    currencies,
    currenciesBySymbol,
    strategies,
    subaccounts,
    aggregations,
    rollupMembershipsByParentChild,
    spotTradingPairsByBaseCurrency,
    spotTradingPairsByQuoteCurrency,
    securitiesBySymbol,
    searchableSecurities,
    positionHedgerHomeCurrency,
    crossAssetRiskGroups,
    enableHedgeRuleCrossAssetRiskGroup,
    enableCrossAssetRiskGroupDeltaRiskCheck,
  ]);
}
